import React, {memo, useCallback, useMemo, useEffect, useState} from 'react'
import { Button, Container, Divider, Grid, TextField, Stack, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent, Autocomplete, Chip } from '@mui/material'
import dayjs, {Dayjs} from 'dayjs'

import {IBrokerRecordsActions, IBrokerRecordsParams} from '../atom/inputs/brokerRecordTypes'
import AutoCompleteSelect from '../atom/AutoCompleteSelect'
import {BROKER_EXECUTION_STATUSES, BROKER_STATUSES, BOOLEANOPTIONS, REMOVALROLEMAPPING, REMOVALROLESOPTIONS} from '@meprism/shared/src/utils/constants'
import CustomDatePicker from '../atom/inputs/DatePicker'
import {exportBrokerRecords} from '../../../redux/brokerService'
import {useAppDispatch, useAppSelector} from '../../../redux/storeExports'
import RetriggerScansPopup from './RetriggerScansPopup'
import RetriggerRemovalsPopup from './RetriggerRemovalsPopup'
import RetriggerInviteEmail from './RetriggerInviteEmail'
import AddBrokerRecordPopup from "./AddBrokerRecordPopup";
import { setRecordType } from '../../../redux/RecordTypes/RecordTypeSlice';
import { RecordTypeEnum } from '../../../redux/RecordTypes/RecordTypeTypes';
import { fetchCognitoUsers } from '@meprism/shared/src/redux/authentication/authenticationSlice'
import { getAuthenticatedUser, getUserNameFromToken } from '../../../services/AuthenticationService'

export interface BrokerOption {
	title: string
	id: string
}

interface FilterContainerProps {
	brokerRecordsReqParams: IBrokerRecordsParams
	dataBrokerOptions: BrokerOption[]
	handleRefetch: () => void
	dispatchBrokerRecordsReqParams: React.Dispatch<IBrokerRecordsActions>
	disableBroker: boolean
	shouldDisplayRecordType?: boolean
}

const FilterContainer = ({
													 dataBrokerOptions,
													 handleRefetch,
													 brokerRecordsReqParams,
													 dispatchBrokerRecordsReqParams,
													 disableBroker,
													 shouldDisplayRecordType = false
												 }: FilterContainerProps) => {
	const {filters} = brokerRecordsReqParams
	const appDispatch = useAppDispatch()
	const startDate = useMemo(() => dayjs(filters.startDate), [filters.startDate])
	const endDate = useMemo(() => dayjs(filters.endDate), [filters.endDate])
	const minEndDate = useMemo(() => dayjs(filters.startDate) || dayjs(), [filters.startDate])
	const selectedRecordType = useAppSelector((state) => state.recordType.selected);
	const [cognitoUsers, setCognitoUsers] = useState<Array<any>>([])
	const [assigneeOptions, setAssigneeOptions] = useState<Array<string>>([])
	const [groupOptions, setGroupOptions] = useState<Array<string>>([])
	const [usersOptions, setUsersOptions] = useState<Array<string>>([]);
	const getCognitoUsers = useCallback(async () => {
		const response = await appDispatch(fetchCognitoUsers()).unwrap()
		setCognitoUsers(response)
	},[appDispatch])

	const getRemovalRoleKeyFromValue = (value: string) =>
		Object.keys(REMOVALROLEMAPPING).find(
		key => REMOVALROLEMAPPING[key as keyof typeof REMOVALROLEMAPPING] === value
	)
		
	useEffect(()=>{
		getCognitoUsers()
	},[getCognitoUsers])

	const getUsersOptions = useCallback(async () => {
		const assigneeAttributes = await getAssigneeAttributes();

		if (
			assigneeAttributes["removalGroup"] &&
			assigneeAttributes["removalGroup"] === "removal_specialist"
		) {
			setUsersOptions([ "N/A", assigneeAttributes["userName"]])
		} else {
			if (!cognitoUsers || cognitoUsers.length === 0) setUsersOptions(["N/A", assigneeAttributes["userName"]])
			else{
				const users = ["N/A", ...cognitoUsers.map(user => user.Username)];
				setUsersOptions(users);
			}
		}
	}, [cognitoUsers]);


	const getAssigneeAttributes = async(): Promise<any> =>{
		const user = await getAuthenticatedUser()
		const userName = await getUserNameFromToken()
		return {
			"userName" : userName,
			"removalGroup" :  user.userInfo.removalRole
		}
	}

	const arraysEqual = (a: any[] = [], b: any[] = []) =>
	  a.length === b.length && a.every((val, i) => val === b[i]);


	const getAssigneeOptions = useCallback(async () => {
		const assigneeAttributes = await getAssigneeAttributes();

		let newAssigneeOptions: string[] = [];
		let newGroupOptions: string[] = [];

		if (
			assigneeAttributes["removalGroup"] &&
			assigneeAttributes["removalGroup"] === "removal_specialist"
		) {
			newGroupOptions = ["N/A", "Removal Specialist"];
			newAssigneeOptions = ["N/A", assigneeAttributes["userName"]];
		} else {
			if (cognitoUsers && cognitoUsers.length !== 0) {
				newAssigneeOptions = ["N/A",...cognitoUsers
					.filter((user) => {
						if (!filters.removal_group) return true;

						const role = user.Attributes?.["custom:removal_role"];

						if (filters.removal_group.includes("N/A") && !role) {
							return true;
						}

						return role ? filters.removal_group.includes(role) : false;
					})
					.map((user) => user.Username)]
			}

			newGroupOptions = REMOVALROLESOPTIONS;
		}

			setAssigneeOptions((prev) =>
				arraysEqual(prev, newAssigneeOptions) ? prev : newAssigneeOptions
			)

			setGroupOptions((prev) =>
				arraysEqual(prev, newGroupOptions) ? prev : newGroupOptions
			)
		}, [cognitoUsers, filters.removal_group]);

	useEffect(()=>{
		getAssigneeOptions()
	}, [cognitoUsers, filters.removal_group, getAssigneeOptions])

	useEffect(()=>{
		getUsersOptions()
	},[cognitoUsers, getUsersOptions])

	const handleFilterChange = useCallback(
		(filterKey: string, value: string | any[] | Dayjs | null | boolean) => {
			dispatchBrokerRecordsReqParams({
				type: 'UPDATE_FILTERS',
				payload: {
					[filterKey]: value
				}
			})
		},
		[dispatchBrokerRecordsReqParams]
	)

	useEffect(() => {
		if (filters.removal_group && filters.removal_assignee) {

			const updatedAssignees = filters.removal_assignee.filter(
				(assignee) => assigneeOptions.includes(assignee)
			);
			if (updatedAssignees.length !== filters.removal_assignee.length) {
				handleFilterChange(
					"removal_assignee",
					updatedAssignees.length ? updatedAssignees : null
				);
			}
		}
	}, [filters.removal_group, assigneeOptions, filters.removal_assignee, handleFilterChange]);

	const handleRecordTypeChange = useCallback(
		(e: SelectChangeEvent<string>) => {
			const recordType = e.target.value as RecordTypeEnum;
			appDispatch(setRecordType(recordType));
			handleFilterChange('recordType', recordType);
		},
		[appDispatch, handleFilterChange]
	);

	const handleExport = useCallback(
		() => appDispatch(exportBrokerRecords(brokerRecordsReqParams)),
		[appDispatch, brokerRecordsReqParams]
	)

	return (
		<Container>
			<Grid container spacing={4} justifyContent="center" alignItems="center">
				<Grid item lg={4} md={6} xs={12}>
					<TextField
						value={filters.muid}
						onChange={(e) => handleFilterChange('muid', e.target.value)}
						label="Search MUID"
						size="small"
						fullWidth
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<AutoCompleteSelect
						options={dataBrokerOptions}
						onChange={(values) => handleFilterChange('brokerNames', values)}
						label="Data Broker"
						value={filters.brokerNames}
						disabled={disableBroker}
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<AutoCompleteSelect
						options={BROKER_EXECUTION_STATUSES}
						onChange={(values) => handleFilterChange('executionStatuses', values)}
						value={filters.executionStatuses}
						label="Execution Status"
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<AutoCompleteSelect
						options={BROKER_STATUSES}
						onChange={(values) => handleFilterChange('statuses', values)}
						label="Status"
						value={filters.statuses}
					/>
				</Grid>
				{
				shouldDisplayRecordType && (
				<Grid item lg={2} md={3} xs={11}>
					<FormControl fullWidth size="small">
						<InputLabel>Record Type</InputLabel>
						<Select
						value={selectedRecordType}
						onChange={handleRecordTypeChange}
						label="Record Type"
						>
							<MenuItem value={RecordTypeEnum.REAL}>Real</MenuItem>
							<MenuItem value={RecordTypeEnum.TEST}>Test</MenuItem>
							<MenuItem value={RecordTypeEnum.ALL}>All</MenuItem>
						</Select>
					</FormControl>
				</Grid>)
				}
				<Grid item lg={3} md={4} xs={12}>
					<CustomDatePicker
						label="Start Date"
						value={startDate}
						onChange={(values) => handleFilterChange('startDate', values)}
						disableFuture={true}
					/>
				</Grid>
				<Grid item lg={3} md={4} xs={12}>
					<CustomDatePicker
						label="End Date"
						value={endDate}
						minDate={minEndDate}
						onChange={(values) => handleFilterChange('endDate', values)}
					/>
				</Grid>
				<Grid item lg={4} md={4} xs={12}>
					<Autocomplete
						options={BOOLEANOPTIONS}
						value={filters.false_positive===true? "True": filters.false_positive===false?"False": "All"}
						onChange={(event, newValue) => {
							handleFilterChange('false_positive', (newValue && newValue === "True")? true:  (newValue && newValue === "False")? false: null)						
						}}
						disableClearable
						renderInput={(params) => (
						<TextField
							{...params}
							label="False Positive"
						/>
						)}
						fullWidth
						isOptionEqualToValue={(option, value) => option === value}
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<Autocomplete
						multiple
						options={groupOptions}
						value={
							filters.removal_group
							? filters.removal_group.map((newValue) =>
									getRemovalRoleKeyFromValue(newValue)
								)
							: []
						}
						onChange={(event, newValue) => {
							const mappedValues = newValue.length
							? newValue.map(
								(value) =>
									REMOVALROLEMAPPING[
									value as keyof typeof REMOVALROLEMAPPING
									]
								)
							: null;          

							handleFilterChange("removal_group", mappedValues);
						}}
						clearOnEscape
						filterSelectedOptions
						renderInput={(params) => (
							<TextField {...params} label="Removal Group" />
						)}
						fullWidth
						limitTags={2}
						isOptionEqualToValue={(option, value) => option === value}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
							<Chip label={option} {...getTagProps({ index })} key={option} />
							))
						}
						/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<Autocomplete
						multiple
						options={assigneeOptions}
						value={filters.removal_assignee ?? []}
						onChange={(event, newValue) => {
							handleFilterChange("removal_assignee", newValue.length ? newValue
								: null
							)
						}}
						clearOnEscape
						filterSelectedOptions
						renderInput={(params) => (
						<TextField
							{...params}
							label="Removal Assignee"
						/>
						)}
						fullWidth
						limitTags={2}
						isOptionEqualToValue={(option, value) => option === value}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
								<Chip label={option} {...getTagProps({ index })} />
							))
						}
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<Autocomplete
						multiple
						options={usersOptions}
						value={filters.status_updated_by ?? []}
						onChange={(event, newValue) => {
							handleFilterChange("status_updated_by", newValue.length ? newValue : null);
						}}
						clearOnEscape
						filterSelectedOptions
						renderInput={(params) => (
						<TextField
							{...params}
							label="Status Updated By"
						/>
						)}
						fullWidth
						limitTags={1}
						isOptionEqualToValue={(option, value) => option === value}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
								<Chip label={option} {...getTagProps({ index })} />
							))
						}
					/>
				</Grid>
				<Grid item lg={4} md={6} xs={12}>
					<Autocomplete
						multiple
						options={usersOptions}
						value={filters.execution_status_updated_by ?? []}
						onChange={(event, newValue) => {
							handleFilterChange("execution_status_updated_by", newValue.length ? newValue : null);
						}}
						clearOnEscape
						filterSelectedOptions
						renderInput={(params) => (
						<TextField
							{...params}
							label="Execution Status Updated By"
						/>
						)}
						fullWidth
						limitTags={1}
						isOptionEqualToValue={(option, value) => option === value}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
								<Chip label={option} {...getTagProps({ index })} />
							))
						}
					/>
				</Grid>
			</Grid>
		<Divider sx={{mt: 5, mb: 5}}/>
		<Stack 
			direction="row" 
			spacing={2} 
			justifyContent="flex-end" 
			alignItems="center"
			sx={{
				flexWrap: 'wrap',
				gap: 2,
			}}
		>
			<Button
				variant="contained"
				color="primary"
				onClick={() => handleRefetch()}
				sx={{minWidth: '150px', height: '42px'}}
			>
				Search
			</Button>
			<Button
				variant="outlined"
				color="secondary"
				onClick={handleExport}
				sx={{minWidth: '150px'}}
			>
				Export
			</Button>
			<RetriggerScansPopup/>
			<RetriggerRemovalsPopup/>
			<RetriggerInviteEmail />
			<AddBrokerRecordPopup/>
		</Stack>
		<Divider sx={{mt: 6, mb: 6}}/>
		</Container>
	)
}

export default memo(FilterContainer)
